import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useToast } from '../context/ToastContext';

export const useIngredients = ({ activeApi, token, setConfirmationModal, onIngredientDeleted }) => {
  const { showToast } = useToast();
  const [ingredients, setIngredients] = useState([]);
  const [isIngredientModalOpen, setIsIngredientModalOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const hasFetchedData = useRef(false);

  useEffect(() => {
    if (token) {
      if (!hasFetchedData.current) {
        hasFetchedData.current = true;
        activeApi.getIngredients()
          .then(setIngredients)
          .catch(error => {
            console.error("Error fetching ingredients:", error);
            showToast("Failed to load ingredients", "error");
          });
      }
    } else {
      hasFetchedData.current = false;
    }
  }, [token, activeApi]);

  const addIngredient = async (ingredient, andAnother) => {
    const isEditing = !!editingIngredient;
    const existingIngredient = isEditing
      ? ingredients.find(i => i.name.toLowerCase() === ingredient.name.toLowerCase() && i.id !== editingIngredient.id)
      : ingredients.find(i => i.name.toLowerCase() === ingredient.name.toLowerCase());

    if (existingIngredient && !isEditing) { // If it exists and we are not editing, return an error
      return { error: 'An ingredient with this name already exists.' };
    }
    if (existingIngredient && isEditing) { // If it exists and we are editing, return an error
      return { error: 'An ingredient with this name already exists.' };
    }

    try {
      const savedIngredient = editingIngredient
        ? await activeApi.updateIngredientAPI(editingIngredient.id, ingredient)
        : await activeApi.addIngredientAPI(ingredient); // Removed client-side ID generation

      if (editingIngredient) {
        setIngredients(prev => prev.map(i => (i.id === savedIngredient.id ? savedIngredient : i)));
        setEditingIngredient(null);
      } else {
        setIngredients(prev => [...prev, savedIngredient]);
      }
      if (!andAnother) {
        setIsIngredientModalOpen(false);
      }
      return { success: true, ingredient: savedIngredient };
    } catch (error) {
      return { error: error.message };
    }
  };

  const startEditingIngredient = (ingredient) => {
    setEditingIngredient(ingredient);
    setIsIngredientModalOpen(true);
  };

  const cancelEditingIngredient = () => {
    setEditingIngredient(null);
    setIsIngredientModalOpen(false);
  };

  const openAddIngredientModal = () => {
    setEditingIngredient(null);
    setIsIngredientModalOpen(true);
  }

  const deleteIngredient = useCallback((ingredientId) => {
    const ingredientToDelete = ingredients.find(i => i.id === ingredientId);
    if (!ingredientToDelete) return;

    // The check for whether an ingredient is in use has been removed to break a circular dependency with the useRecipes hook.
    // The backend should handle any cascading deletes or constraints. A more advanced implementation could
    // involve passing a function to get the recipes state, but this is simpler and effective.
    const message = `Are you sure you want to delete ingredient '${ingredientToDelete.name}'?`;

    setConfirmationModal({
      isOpen: true,
      message,
      onConfirm: () => {
        activeApi.deleteIngredientAPI(ingredientId)
          .then(() => {
            const newIngredients = ingredients.filter(i => i.id !== ingredientId);
            setIngredients(newIngredients);
            const totalPages = Math.ceil(newIngredients.length / 5);
            if (page > totalPages) {
              setPage(totalPages || 1);
            }
            if (onIngredientDeleted) {
              onIngredientDeleted(ingredientId);
            }
          })
          .catch(error => {
            console.error('Error deleting ingredient:', error);
            showToast("Failed to delete ingredient", "error");
          })
          .finally(() => setConfirmationModal({ isOpen: false, message: '', onConfirm: () => { } }));
      }
    });
  }, [activeApi, ingredients, page, setConfirmationModal, onIngredientDeleted]);

  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setPage(1);
  };

  const filteredIngredients = useMemo(() => ingredients.filter(ingredient =>
    ingredient.name.toLowerCase().includes(searchTerm.toLowerCase())
  ), [ingredients, searchTerm]);

  return {
    ingredients, filteredIngredients, isIngredientModalOpen, setIsIngredientModalOpen, editingIngredient,
    searchTerm, page, setPage, addIngredient, deleteIngredient, startEditingIngredient,
    cancelEditingIngredient, openAddIngredientModal, handleSearchChange,
  };
};